<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { login } from '../services/auth'
import { apiConfig, request } from '../services/api'

const router = useRouter()
const route = useRoute()

// 旧版 loginForm：username / password / verifycode / remember(默认勾选)
const form = reactive({
  username: localStorage.getItem('username') || '',
  password: localStorage.getItem('password') || '',
  verifycode: '',
  remember: true,
})

const loading = ref(false)
const errorMessage = ref('')
// 旧版 display：控制密码明文/密文（眼睛图标切换）
const display = ref(false)
// 验证码图片地址
const authImg = ref('')
// 旧版 login_page_background：登录页背景，取自服务器配置，回退到本地图
const loginBackground = ref('url(/legacy/images/login-bg.png)')
// 旧版 login-title：显示 settings.app.name（站点名），取自 web_site_title 配置
const appName = ref('')

// 账号、密码和验证码填写完整后才允许登录
const canSubmit = computed(() => form.username.trim() && form.password && form.verifycode.trim())

// 旧版 ConfigApiService.getConfig：POST {apiUrl}/config/detail { name }
async function getConfig(name) {
  const result = await request('/config/detail', {
    server: 'api',
    method: 'POST',
    data: { name },
    form: false,
    anonymous: true,
  })
  return result.data
}

// 旧版 getBackGround()：有配置用配置图，否则回退到 /assets/images/login-bg.png
async function getBackGround() {
  try {
    const data = await getConfig('login_page_background')
    loginBackground.value = data ? `url(${data})` : 'url(/legacy/images/login-bg.png)'
  } catch (_) {
    loginBackground.value = 'url(/legacy/images/login-bg.png)'
  }
}

async function loadTitle() {
  try {
    const data = await getConfig('web_site_title')
    if (typeof data === 'string') appName.value = data
  } catch (_) {
    /* 站点名取不到时按旧版逻辑不显示标题 */
  }
}

// 旧版 changImg()：刷新验证码图片，地址为 authUrl + /verify/image
function changImg() {
  form.verifycode = ''
  authImg.value = `${apiConfig.authServer}/verify/image?t=${Math.random()}`
}

// 勾选记住密码则存账号密码，否则清空；登录失败时刷新验证码
async function submit() {
  if (!canSubmit.value || loading.value) return
  loading.value = true
  errorMessage.value = ''

  if (form.remember) {
    localStorage.setItem('username', form.username)
    localStorage.setItem('password', form.password)
  } else {
    localStorage.setItem('username', '')
    localStorage.setItem('password', '')
  }

  try {
    await login({
      username: form.username.trim(),
      password: form.password,
      verifycode: form.verifycode,
    })
    await router.replace(typeof route.query.redirect === 'string' ? route.query.redirect : '/tabs')
  } catch (error) {
    errorMessage.value = error.message || '登录失败，请稍后再试。'
    // 登录失败后刷新验证码图片
    changImg()
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  changImg()
  getBackGround()
  loadTitle()
})
</script>

<template>
  <div class="login-content" :style="{ backgroundImage: loginBackground }">
    <div v-if="appName" class="login-title">{{ appName }}</div>

    <div class="login-form">
      <form novalidate @submit.prevent="submit">
        <div class="input-line">
          <span class="ion-label">账号：</span>
          <input
            v-model="form.username"
            class="ion-input"
            type="text"
            placeholder="账号或手机号码"
            autocomplete="username"
          />
        </div>

        <div class="input-line">
          <span class="ion-label">密码：</span>
          <input
            v-model="form.password"
            class="ion-input"
            :type="display ? 'text' : 'password'"
            placeholder="密码"
            autocomplete="current-password"
          />
          <span class="eye-icon" @click="display = !display">
            <!-- 旧版 ion-icon name="eye-off"（display=true 时显示），取自 ionicons md-eye-off -->
            <svg v-if="display" viewBox="0 0 512 512" width="20" height="20">
              <path fill="currentColor" d="M256.1 144.8c56.2 0 101.9 45.3 101.9 101.1 0 13.1-2.6 25.5-7.3 37l59.5 59c30.8-25.5 55-58.4 69.9-96-35.3-88.7-122.3-151.6-224.2-151.6-28.5 0-55.8 5.1-81.1 14.1l44 43.7c11.6-4.6 24.1-7.3 37.3-7.3zM52.4 89.7l46.5 46.1 9.4 9.3c-33.9 26-60.4 60.8-76.3 100.8 35.2 88.7 122.2 151.6 224.1 151.6 31.6 0 61.7-6.1 89.2-17l8.6 8.5 59.7 59 25.9-25.7L78.2 64 52.4 89.7zM165 201.4l31.6 31.3c-1 4.2-1.6 8.7-1.6 13.1 0 33.5 27.3 60.6 61.1 60.6 4.5 0 9-.6 13.2-1.6l31.6 31.3c-13.6 6.7-28.7 10.7-44.8 10.7-56.2 0-101.9-45.3-101.9-101.1 0-15.8 4.1-30.7 10.8-44.3zm87.8-15.7l64.2 63.7.4-3.2c0-33.5-27.3-60.6-61.1-60.6l-3.5.1z"/>
            </svg>
            <!-- 旧版 ion-icon name="eye"（display=false 时显示），取自 ionicons md-eye -->
            <svg v-else viewBox="0 0 512 512" width="20" height="20">
              <path fill="currentColor" d="M256 105c-101.8 0-188.4 62.4-224 151 35.6 88.6 122.2 151 224 151s188.4-62.4 224-151c-35.6-88.6-122.2-151-224-151zm0 251.7c-56 0-101.8-45.3-101.8-100.7S200 155.3 256 155.3 357.8 200.6 357.8 256 312 356.7 256 356.7zm0-161.1c-33.6 0-61.1 27.2-61.1 60.4s27.5 60.4 61.1 60.4 61.1-27.2 61.1-60.4-27.5-60.4-61.1-60.4z"/>
            </svg>
          </span>
        </div>

        <div class="input-line">
          <input
            v-model="form.verifycode"
            class="ion-input"
            type="text"
            placeholder="验证码"
          />
          <span class="auth-img">
            <img :src="authImg" tappable @click="changImg" alt="验证码" />
          </span>
        </div>

        <div class="login-options">
          <div class="login-remember">
            <input v-model="form.remember" type="checkbox" class="remember-checkbox" />
            <span>记住密码</span>
          </div>

          <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
        </div>

        <div class="login-action">
          <button class="login-button" type="submit" :disabled="!canSubmit || loading">
            {{ loading ? '正在登录，请稍等' : '登 录' }}
          </button>
        </div>

        <div class="reg-action">
          <RouterLink to="/register">注册账号</RouterLink>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
/* 以下样式严格对应旧版 chunk 31 的组件内联样式 */
.login-content {
  background-size: 100% 100%;
  width: 100vw;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
}
.login-content .login-title {
  color: #fff;
  font-size: 2rem;
  text-shadow: 0px 2px 3px rgba(0, 0, 0, 0.3);
}
.login-content .login-form {
  margin-bottom: -6rem;
  padding: 20px;
  width: 100%;
}
.login-content .login-form .input-line {
  color: #333;
  border: 1px solid #fff;
  border-radius: 10px;
  margin-top: 20px;
  margin-bottom: 20px;
  position: relative;
  background: rgba(255, 255, 255, 0.2);
  padding-left: 15px;
  display: flex;
  align-items: center;
}
.login-content .login-form .input-line .ion-label {
  color: #fff;
  white-space: nowrap;
}
/* ion-input 在旧版中透明无边框，边框和背景由 .input-line 承担 */
.login-content .login-form .input-line .ion-input {
  flex: 1;
  min-width: 0;
  height: 44px;
  border: 0;
  outline: none;
  background: transparent;
  color: #333;
  font-size: 1rem;
  padding: 0 8px;
}
.login-content .login-form .input-line .ion-input::placeholder {
  color: rgba(51, 51, 51, 0.5);
}
.login-content .login-form .input-line .eye-icon {
  position: absolute;
  right: 8px;
  top: 25%;
  font-size: 20px;
  color: #111;
  z-index: 1000;
  cursor: pointer;
  display: inline-flex;
}
.login-content .login-form .login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 18px;
}
.login-content .login-form .login-remember {
  display: flex;
  color: #fff;
  font-size: 0.8rem;
  align-items: center;
}
.login-content .login-form .login-remember .remember-checkbox {
  border: none;
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: #f95454;
}
.login-content .login-form .login-remember span {
  display: inline-block;
  padding-left: 5px;
}
.login-content .login-form .login-action {
  margin-top: 30px;
}
/* ion-button color="secondary" expand="full"：青色全宽按钮 */
.login-content .login-form .login-action .login-button {
  display: block;
  width: 100%;
  min-height: 2.8em;
  border: 0;
  border-radius: 4px;
  background: #fcf8f88f;
  color: #be2e30;
  font-size: 1rem;
  letter-spacing: 1px;
  font-weight:600;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
}
.login-content .login-form .login-action .login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.login-content .login-form .auth-img {
  min-width: 120px;
  max-width: 50%;
  position: absolute;
  height: 100%;
  border-top-right-radius: 20px;
  right: 0;
  bottom: 0;
}
.login-content .login-form .auth-img img {
  border-radius: 0 10px 10px 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  cursor: pointer;
}
.login-content .login-form .reg-action {
  margin-top: 20px;
  cursor: pointer;
  font-size: 0.8rem;
  color: #fff;
}
.login-content .login-form .reg-action a {
  text-decoration: none;
  color: #fff;
}
.login-content .login-form .form-error {
  margin: 0;
  color: #fff3f3;
  font-size: 0.85rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  text-align: right;
}
</style>
